import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import type { FuelVariant } from '@tucupet/shared';
import { PageHeader, Table, Btn, Modal, Field, Select } from '../components/ui';
import { api } from '../lib/api';

type V = FuelVariant & { _id?: string };

export default function VariantsPage() {
  const qc = useQueryClient();
  const [sp, setSp] = useSearchParams();
  const family = sp.get('family') ?? '';
  const { data, isLoading } = useQuery<V[]>({ queryKey: ['admin', 'variants'], queryFn: () => api('/api/admin/variants') });
  const [editing, setEditing] = useState<Partial<V> | null>(null);

  const filtered = useMemo(() => {
    const sorted = [...(data ?? [])].sort((a, b) => {
      if (a.family !== b.family) return a.family.localeCompare(b.family);
      return (a.order ?? 0) - (b.order ?? 0);
    });
    return family ? sorted.filter((v) => v.family === family) : sorted;
  }, [data, family]);

  const setOrder = useMutation({
    mutationFn: async (input: { id: string; order: number }) => api(`/api/admin/variants/${input.id}`, { method: 'PATCH', body: { order: input.order } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'variants'] }),
  });
  const swapOrder = (row: V, direction: 'up' | 'down') => {
    const sameFamily = filtered.filter((v) => v.family === row.family);
    const idx = sameFamily.findIndex((v) => (v as any)._id === (row as any)._id);
    const swapWith = direction === 'up' ? sameFamily[idx - 1] : sameFamily[idx + 1];
    if (!swapWith) return;
    const a = (row as any)._id as string;
    const b = (swapWith as any)._id as string;
    setOrder.mutate({ id: a, order: swapWith.order ?? 0 });
    setOrder.mutate({ id: b, order: row.order ?? 0 });
  };

  const save = useMutation({
    mutationFn: async (v: Partial<V>) => {
      const payload = { family: v.family, name: v.name, octane: v.octane, price: v.price, tag: v.tag, desc: v.desc, comingSoon: v.comingSoon ?? false, order: v.order ?? 0 };
      if (v._id) return api(`/api/admin/variants/${v._id}`, { method: 'PATCH', body: payload });
      return api('/api/admin/variants', { method: 'POST', body: payload });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'variants'] });
      setEditing(null);
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api(`/api/admin/variants/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'variants'] }),
  });

  return (
    <div>
      <PageHeader kicker="CATÁLOGO" title={`Combustibles · ${filtered.length}`} sub="Precios globales por variante. Reordenar con ↑↓ dentro de cada familia." action={<Btn onClick={() => setEditing({ family: 'gasolina', name: '', octane: '', price: 0, tag: '', desc: '', comingSoon: false, order: 0 })}>+ Nueva variante</Btn>} />

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 220 }}>
          <Select<string>
            label="Familia"
            value={family}
            onChange={(v) => { const m: Record<string, string> = {}; if (v) m.family = v; setSp(m); }}
            options={[
              { value: '', label: 'Todas' },
              { value: 'gasolina', label: 'Gasolina' },
              { value: 'diesel', label: 'Diésel' },
              { value: 'ev', label: 'Eléctrico' },
            ]}
          />
        </div>
      </div>

      <Table<V>
        rows={filtered}
        empty={isLoading ? 'Cargando…' : 'Sin variantes en este filtro.'}
        columns={[
          {
            key: 'order',
            label: 'Orden',
            width: 80,
            render: (r) => {
              const sameFamily = filtered.filter((v) => v.family === r.family);
              const idx = sameFamily.findIndex((v) => (v as any)._id === (r as any)._id);
              return (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <button
                    onClick={() => swapOrder(r, 'up')}
                    disabled={idx <= 0 || setOrder.isPending}
                    title="Subir dentro de la familia"
                    style={iconBtn(idx <= 0)}
                  >
                    ↑
                  </button>
                  <button
                    onClick={() => swapOrder(r, 'down')}
                    disabled={idx >= sameFamily.length - 1 || setOrder.isPending}
                    title="Bajar dentro de la familia"
                    style={iconBtn(idx >= sameFamily.length - 1)}
                  >
                    ↓
                  </button>
                </div>
              );
            },
          },
          { key: 'family', label: 'Familia', render: (r) => <span className="mono" style={{ fontSize: 11, color: 'var(--text-2)' }}>{r.family.toUpperCase()}</span> },
          { key: 'name', label: 'Nombre', render: (r) => <span style={{ fontWeight: 600 }}>{r.name}</span> },
          { key: 'octane', label: 'Octanaje', render: (r) => <span className="mono">{r.octane}</span> },
          { key: 'price', label: 'Precio', render: (r) => <span className="mono" style={{ color: 'var(--accent)', fontWeight: 600 }}>${r.price.toFixed(2)}</span> },
          { key: 'tag', label: 'Tag', render: (r) => r.tag || '—' },
          { key: 'soon', label: 'Próx.', render: (r) => (r.comingSoon ? '✓' : '—') },
          {
            key: 'actions',
            label: '',
            render: (r) => (
              <div style={{ display: 'flex', gap: 6 }}>
                <Btn size="sm" variant="ghost" onClick={() => setEditing(r)}>Editar</Btn>
                <Btn size="sm" variant="danger" onClick={() => confirm('¿Desactivar?') && remove.mutate((r as any)._id ?? r.id)}>Borrar</Btn>
              </div>
            ),
          },
        ]}
      />

      <VariantModal open={!!editing} initial={editing} onClose={() => setEditing(null)} onSubmit={(v) => save.mutate(v)} />
    </div>
  );
}

function iconBtn(disabled: boolean): React.CSSProperties {
  return {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    color: disabled ? 'var(--text-3)' : 'var(--text)',
    width: 28, height: 28, borderRadius: 8,
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontFamily: 'inherit',
    fontSize: 14,
  };
}

function VariantModal({ open, initial, onClose, onSubmit }: { open: boolean; initial: Partial<V> | null; onClose: () => void; onSubmit: (v: Partial<V>) => void }) {
  const [family, setFamily] = useState<'gasolina' | 'diesel' | 'ev'>('gasolina');
  const [name, setName] = useState('');
  const [octane, setOctane] = useState('');
  const [price, setPrice] = useState(0);
  const [tag, setTag] = useState('');
  const [desc, setDesc] = useState('');
  const [order, setOrder] = useState(0);
  const [comingSoon, setComingSoon] = useState(false);

  useEffect(() => {
    if (initial) {
      setFamily(initial.family ?? 'gasolina');
      setName(initial.name ?? '');
      setOctane(initial.octane ?? '');
      setPrice(initial.price ?? 0);
      setTag(initial.tag ?? '');
      setDesc(initial.desc ?? '');
      setOrder(initial.order ?? 0);
      setComingSoon(initial.comingSoon ?? false);
    }
  }, [initial]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={initial?.id ? 'Editar variante' : 'Nueva variante'}
      footer={
        <>
          <Btn variant="ghost" onClick={onClose}>Cancelar</Btn>
          <Btn onClick={() => onSubmit({ ...(initial as any), family, name, octane, price, tag: tag || null, desc, order, comingSoon })}>Guardar</Btn>
        </>
      }
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Select<'gasolina' | 'diesel' | 'ev'>
          label="Familia"
          value={family}
          onChange={setFamily}
          options={[
            { value: 'gasolina', label: 'Gasolina' },
            { value: 'diesel', label: 'Diésel' },
            { value: 'ev', label: 'Eléctrico' },
          ]}
        />
        <Field label="Nombre" value={name} onChange={setName} />
        <Field label="Octanaje" value={octane} onChange={setOctane} placeholder="95 oct / Pro / DC 50" />
        <Field label="Precio (USD)" value={price} onChange={(v) => setPrice(parseFloat(v))} type="number" step="0.01" />
        <Field label="Tag" value={tag} onChange={setTag} placeholder="Premium / Recomendado…" />
        <Field label="Orden" value={order} onChange={(v) => setOrder(parseInt(v))} type="number" />
        <div style={{ gridColumn: '1 / -1' }}>
          <Field label="Descripción" value={desc} onChange={setDesc} fullWidth />
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 24 }}>
          <input type="checkbox" checked={comingSoon} onChange={(e) => setComingSoon(e.target.checked)} />
          <span style={{ fontSize: 13 }}>Próximamente</span>
        </label>
      </div>
    </Modal>
  );
}
